type PositionedText = { str: string; transform: number[]; width: number };

const SAME_LINE_TOLERANCE = 2;

// Rebuilds rows with columns padded into place, so side-by-side blocks (Bill To / Ship To /
// Ship From) stay under their headers instead of collapsing into one unlabeled run.
export function layoutPageText(items: PositionedText[]): string {
  const words = items.filter((item) => item.str.trim() !== "");
  if (words.length === 0) return "";

  const charWidths = words.map((w) => w.width / w.str.length).sort((a, b) => a - b);
  const charWidth = charWidths[Math.floor(charWidths.length / 2)] || 1;
  const left = Math.min(...words.map((w) => w.transform[4]));

  const rows: PositionedText[][] = [];
  [...words]
    .sort((a, b) => b.transform[5] - a.transform[5] || a.transform[4] - b.transform[4])
    .forEach((word) => {
      const row = rows[rows.length - 1];
      if (row && Math.abs(row[0].transform[5] - word.transform[5]) <= SAME_LINE_TOLERANCE) {
        row.push(word);
      } else {
        rows.push([word]);
      }
    });

  return rows
    .map((row) => {
      let line = "";
      let prevEnd: number | null = null;
      row
        .sort((a, b) => a.transform[4] - b.transform[4])
        .forEach((word) => {
          const x = word.transform[4];
          const column = Math.round((x - left) / charWidth);
          const abutting = prevEnd != null && x - prevEnd < charWidth * 0.3;
          const pad = abutting ? 0 : Math.max(column - line.length, line ? 1 : 0);
          line += " ".repeat(pad) + word.str;
          prevEnd = x + word.width;
        });
      return line.trimEnd();
    })
    .join("\n");
}

// pdfjs-dist is lazy-imported so the ~1MB parser + worker only ship on first use.
export async function extractPdfText(file: File): Promise<string> {
  const { getDocument } = await import("./pdfjsClient");
  const buffer = await file.arrayBuffer();
  const doc = await getDocument({ data: new Uint8Array(buffer) }).promise;
  const pages: string[] = [];
  for (let i = 1; i <= doc.numPages; i += 1) {
    const page = await doc.getPage(i);
    const content = await page.getTextContent();
    pages.push(layoutPageText(content.items.filter((item) => "str" in item)));
  }
  return pages.join("\n\n");
}
